// 画像ファイルのプレビュー
import IconDownload from '@src/assets/images/common/icon/chat-action/icon-download.svg';
import PhotoGrayIcon from '@src/assets/images/common/icon/photo-gray.svg';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useIsModal } from '@src/context/IsModalContext';
import AttachmentActionMenuSp from '@src/features/shared/community/components/chat/AttachmentActionMenuSp';
import AttachmentErrorModal from '@src/features/shared/community/components/chat/chatList/AttachmentErrorModal';
import { ChatAttachmentItemProps } from '@src/features/shared/community/components/chat/chatList/ChatAttachmentList';
import { TOOLTIP_DISPLAY_TIME } from '@src/features/shared/community/components/chat/chatList/ChatItemHeader';
import PreviewImageModal from '@src/features/shared/community/components/chat/PreviewImageModal';
import useFileDownload from '@src/hooks/useFileDownload';
import useIsMobile from '@src/hooks/useIsMobile';
import { useState } from 'react';

const ChatImageItem = ({
  attachment,
  chat,
  showTooltip,
  displayTooltip,
  allowImagePreview = true,
}: ChatAttachmentItemProps) => {
  const { downloadFile } = useFileDownload();
  const [isImageActionMenuSpOpen, setIsImageActionMenuSpOpen] = useState<boolean>(false);
  const [isPreviewImageModalOpen, setIsPreviewImageModalOpen] = useState<boolean>(false);
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string | null>(null);
  const [attachmentErrorModalOpen, setAttachmentErrorModalOpen] = useState(false);
  const [attachmentErrorMessage, setAttachmentErrorMessage] = useState('');
  const { profile } = useSharedAuth();
  const isLoggedIn = profile?.companyProfile?.isLogin ?? profile?.tenantProfile?.isLogin;
  const { openModal: globalOpenModal, closeModal: globalCloseModal } = useIsModal();
  const isMobile = useIsMobile();
  const PRESS_LONG_TIME = 600;
  let touchTimer: NodeJS.Timeout;

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (!allowImagePreview) {
      return;
    }

    const loadImage = (src: string, fallbackSrc?: string) => {
      const image = new Image();
      image.onload = () => {
        setResolvedImageUrl(src);
        setIsPreviewImageModalOpen(true);
        globalOpenModal();
      };
      image.onerror = () => {
        if (fallbackSrc) {
          loadImage(fallbackSrc);
          return;
        }
        setAttachmentErrorMessage('ファイルが見つかりません');
        setAttachmentErrorModalOpen(true);
      };
      image.src = src;
    };

    const thumbnailUrl = attachment.thumbnail_url;
    if (thumbnailUrl) {
      loadImage(thumbnailUrl, attachment.file_url);
    } else {
      loadImage(attachment.file_url);
    }
  };

  const handleImageLongPress = () => {
    setIsImageActionMenuSpOpen(true);
    globalOpenModal();
  };

  const handleImageActionMenuClose = () => {
    setIsImageActionMenuSpOpen(false);
    globalCloseModal();
  };

  const handleDownloadClick = async () => {
    try {
      if (!isLoggedIn) {
        displayTooltip?.(TOOLTIP_DISPLAY_TIME);
        return;
      }
      await downloadFile(attachment.file_url, attachment.file_name);
    } catch {
      setAttachmentErrorMessage('ダウンロードに失敗しました。再試行してください。');
      setAttachmentErrorModalOpen(true);
    }
  };

  return (
    <div className='group/chat-image relative size-full'>
      <div
        className='block size-full overflow-hidden rounded-[4px]'
        onContextMenu={(e) => {
          if (isMobile) {
            e.preventDefault();
          }
        }}
        onClick={handleImageClick}
        onTouchStart={(e) => {
          if (e.touches.length === 1) {
            touchTimer = setTimeout(() => {
              handleImageLongPress();
            }, PRESS_LONG_TIME);
          }
        }}
        onTouchEnd={() => {
          clearTimeout(touchTimer);
        }}
        onTouchCancel={() => {
          clearTimeout(touchTimer);
          handleImageActionMenuClose();
        }}
      >
        <img
          src={attachment.thumbnail_url ?? attachment.file_url}
          alt=''
          style={{ WebkitTouchCallout: 'none' }}
          className='aspect-[4/3] size-full object-cover'
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.src = PhotoGrayIcon;
          }}
          role='img'
        />
      </div>
      <button
        className='absolute right-1 top-1 hidden size-10 place-items-center rounded-[4px] bg-white group-hover/chat-image:grid md:!hidden'
        onClick={handleDownloadClick}
      >
        <img
          src={IconDownload}
          alt=''
        />
      </button>
      {isImageActionMenuSpOpen && (
        <AttachmentActionMenuSp
          handleCloseClick={handleImageActionMenuClose}
          handleDownloadClick={() => {
            handleDownloadClick();
          }}
          showTooltip={showTooltip}
          fileUrl={attachment.file_url}
          fileName={attachment.file_name}
        />
      )}
      {isPreviewImageModalOpen && (
        <PreviewImageModal
          attachment={attachment}
          chat={chat}
          imageUrl={resolvedImageUrl ?? undefined}
          onCancel={() => {
            setIsPreviewImageModalOpen(false);
            setResolvedImageUrl(null);
            globalCloseModal();
          }}
        />
      )}
      {attachmentErrorModalOpen && (
        <AttachmentErrorModal
          fileName={attachment.file_name}
          attachmentErrorMessage={attachmentErrorMessage}
          onClose={() => setAttachmentErrorModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ChatImageItem;
